import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, apiDownload, ApiError } from '../api/client';
import { Badge, ErrorBox } from '../components/common';
import { config } from '../config';
import { formatDate } from '../labels';
import { Icon } from '../components/icons';
import '../styles/pilotage.css';

interface Order {
  id: string;
  reference: string;
  amount_xaf: number;
  provider_order_id: string | null;
  status: string;
  expires_at: string;
  confirmed_at: string | null;
  legal_basis: string;
  fee_status: string;
  stamp_number: string | null;
  stamp_status: string | null;
}
interface PaymentInfo {
  fee: { amount_xaf: number; legal_basis: string; status: string } | null;
  orders: Order[];
  exemption: { id: string; legal_basis: string; authority: string; status: string } | null;
}

export const ORDER_STATUS: Record<string, { label: string; tone: string }> = {
  cree: { label: 'Créé', tone: 'muted' },
  en_attente: { label: 'En attente du règlement', tone: 'info' },
  confirme: { label: 'Confirmé', tone: 'ok' },
  echoue: { label: 'Échoué', tone: 'danger' },
  expire: { label: 'Expiré', tone: 'muted' },
  annule: { label: 'Annulé', tone: 'muted' },
  rembourse: { label: 'Remboursé', tone: 'warn' },
};

const xaf = (n: number) => `${n.toLocaleString('fr-FR')} XAF`;

/** Carte de paiement affichée sur une demande (usager, guichet, instruction). */
export function PaymentCard({
  applicationId,
  status,
  role,
  onChange,
}: {
  applicationId: string;
  status: string;
  role: string;
  onChange: () => void;
}) {
  const [info, setInfo] = useState<PaymentInfo | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [exemption, setExemption] = useState({ legalBasis: '', authority: '', evidenceRef: '' });
  const load = useCallback(
    () => api<PaymentInfo>(`/paiements/demandes/${applicationId}`).then(setInfo).catch(setError),
    [applicationId],
  );
  useEffect(() => {
    void load();
  }, [load, status]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
      onChange();
    } catch (e) {
      setError(e as ApiError);
    }
  };

  if (!info) return null;
  const active = info.orders.find((o) => o.status === 'en_attente');
  const confirmed = info.orders.find((o) => o.status === 'confirme');
  if (!info.orders.length && !info.exemption && status !== 'paiement_attendu') return null;

  return (
    <div className="card stack">
      <h2>Droits</h2>
      {info.fee && (
        <p style={{ margin: 0 }}>
          Montant : <strong>{xaf(info.fee.amount_xaf)}</strong>{' '}
        </p>
      )}
      {info.exemption && (
        <p className="alert alert-info small" style={{ margin: 0 }}>
          Exonération{' '}
          {info.exemption.status === 'accordee'
            ? 'accordée'
            : info.exemption.status === 'refusee'
              ? 'refusée'
              : 'demandée'}{' '}
          - {info.exemption.legal_basis}
        </p>
      )}
      {confirmed && (
        <p className="alert alert-ok small" style={{ margin: 0 }}>
          Paiement confirmé par le prestataire. Timbre n° <code>{confirmed.stamp_number}</code>.
        </p>
      )}
      {info.orders.length > 0 && (
        <ul className="small">
          {info.orders.map((o) => (
            <li key={o.id}>
              <code>{o.reference}</code> - {xaf(o.amount_xaf)} -{' '}
              <Badge tone={ORDER_STATUS[o.status]?.tone ?? 'muted'}>
                {ORDER_STATUS[o.status]?.label ?? o.status}
              </Badge>
              {o.status === 'en_attente' && (
                <span className="muted"> (expire le {formatDate(o.expires_at)})</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <ErrorBox error={error} />
      {status === 'paiement_attendu' && role !== 'instructor' && !info.exemption && (
        <div className="stack">
          <p className="small muted" style={{ margin: 0 }}>
            Le règlement se fait sur le canal E-Timbre (mobile money, guichet). Votre demande avance dès que
            le prestataire confirme le paiement à CASIJUD - le retour sur cette page ne suffit pas.
          </p>
          <div className="row">
            <button
              type="button"
              className="btn btn-gold"
              onClick={() =>
                void run(() => api(`/paiements/demandes/${applicationId}/ordre`, { method: 'POST' }))
              }
            >
              {active ? 'Afficher l’ordre de paiement' : 'Payer les droits'}
            </button>
            {config.authMode === 'dev' && active?.provider_order_id && (
              <>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() =>
                    void run(() =>
                      api(`/dev/etimbre/${active.provider_order_id}/simuler`, {
                        method: 'POST',
                        body: { outcome: 'paye' },
                      }),
                    )
                  }
                >
                  Payer avec E-Timbre
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() =>
                    void run(() =>
                      api(`/dev/etimbre/${active.provider_order_id}/simuler`, {
                        method: 'POST',
                        body: { outcome: 'refuse' },
                      }),
                    )
                  }
                >
                  Refuser le paiement
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {status === 'paiement_attendu' && role === 'instructor' && !info.exemption && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() =>
              api(`/paiements/demandes/${applicationId}/exoneration`, { method: 'POST', body: exemption }),
            );
          }}
        >
          <p className="small muted" style={{ margin: 0 }}>
            Exonération légale : base, autorité et preuve ; approuvée ensuite par le service financier.
          </p>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="ex-basis">Disposition</label>
              <input
                id="ex-basis"
                required
                minLength={5}
                value={exemption.legalBasis}
                onChange={(e) => setExemption({ ...exemption, legalBasis: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="ex-auth">Autorité</label>
              <input
                id="ex-auth"
                required
                minLength={2}
                value={exemption.authority}
                onChange={(e) => setExemption({ ...exemption, authority: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="ex-proof">Preuve</label>
              <input
                id="ex-proof"
                required
                minLength={3}
                value={exemption.evidenceRef}
                onChange={(e) => setExemption({ ...exemption, evidenceRef: e.target.value })}
              />
            </div>
          </div>
          <div>
            <button className="btn btn-ghost" type="submit">
              Demander l’exonération
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ---------- Espace financier ----------

export function FinancePage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('onglet') ?? 'ordres';
  const tabs = [
    ['ordres', 'Transactions'],
    ['anomalies', 'Anomalies'],
    ['exonerations', 'Exonérations'],
    ['rapprochement', 'Rapprochement'],
    ['tarifs', 'Barème'],
  ];
  return (
    <div className="stack">
      <div className="head">
        <div>
          <h1>Finance</h1>
          <p className="lead">Droits, paiements, timbres et rapprochements.</p>
        </div>
        <div className="actions pl-head-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void apiDownload('/finance/export.csv', 'paiements-confirmes.csv')}
          >
            Exporter les paiements confirmés (CSV)
          </button>
        </div>
      </div>
      <div className="alert pl-alert">
        <Icon name="shield" />
        <span className="pl-alert-body">Aucune identité ni aucun contenu judiciaire n’est affiché ici.</span>
      </div>
      <nav className="tabs pl-tabs" aria-label="Onglets">
        {tabs.map(([k, l]) => (
          <button
            key={k}
            type="button"
            className="tab"
            aria-pressed={tab === k}
            onClick={() => setParams({ onglet: k! })}
          >
            {l}
          </button>
        ))}
      </nav>
      {tab === 'ordres' && <OrdersTab />}
      {tab === 'anomalies' && <AnomaliesTab />}
      {tab === 'exonerations' && <ExemptionsTab />}
      {tab === 'rapprochement' && <ReconciliationTab />}
      {tab === 'tarifs' && <FeesTab />}
    </div>
  );
}

function OrdersTab() {
  const [rows, setRows] = useState<(Order & { application_reference: string; created_at: string })[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    api<typeof rows>('/finance/ordres').then(setRows).catch(setError);
  }, []);
  return (
    <>
      <ErrorBox error={error} />
      <div className="card np table-wrap">
        <table>
          <caption className="visually-hidden">Transactions</caption>
          <thead>
            <tr>
              <th scope="col">Ordre</th>
              <th scope="col">Demande</th>
              <th scope="col">Montant</th>
              <th scope="col">État</th>
              <th scope="col">Timbre</th>
              <th scope="col">Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  Aucune transaction.
                </td>
              </tr>
            )}
            {rows.map((o) => (
              <tr key={o.id}>
                <td>
                  <span className="mono pl-ref">{o.reference}</span>
                </td>
                <td>
                  <span className="mono pl-ref">{o.application_reference}</span>
                </td>
                <td>{xaf(o.amount_xaf)}</td>
                <td>
                  <Badge tone={ORDER_STATUS[o.status]?.tone ?? 'muted'}>
                    {ORDER_STATUS[o.status]?.label ?? o.status}
                  </Badge>
                </td>
                <td>
                  {o.stamp_number ? (
                    <span className="pl-cell">
                      <span className="mono pl-ref">{o.stamp_number}</span>
                      <span className="pl-sub">{o.stamp_status}</span>
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
                <td>{formatDate(o.confirmed_at ?? o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const ANOMALY_LABELS: Record<string, string> = {
  signature_invalide: 'Signature invalide',
  montant_incoherent: 'Montant incohérent',
  paiement_tardif: 'Paiement tardif',
  double_paiement: 'Double paiement',
  timbre_deja_consomme: 'Timbre déjà consommé',
  ordre_inconnu: 'Ordre inconnu',
  ecart_rapprochement: 'Écart de rapprochement',
};

function AnomaliesTab() {
  const [rows, setRows] = useState<
    {
      id: string;
      kind: string;
      detail: string;
      status: string;
      resolution: string | null;
      created_at: string;
      order_reference: string | null;
    }[]
  >([]);
  const [resolution, setResolution] = useState<Record<string, string>>({});
  const [error, setError] = useState<ApiError | null>(null);
  const load = () => api<typeof rows>('/finance/anomalies').then(setRows).catch(setError);
  useEffect(() => {
    void load();
  }, []);
  const resolve = async (id: string) => {
    try {
      await api(`/finance/anomalies/${id}/traiter`, {
        method: 'POST',
        body: { resolution: resolution[id] ?? '' },
      });
      await load();
    } catch (e) {
      setError(e as ApiError);
    }
  };
  return (
    <>
      <ErrorBox error={error} />
      <div className="card np table-wrap">
        <table>
          <caption className="visually-hidden">Anomalies</caption>
          <thead>
            <tr>
              <th scope="col">Anomalie</th>
              <th scope="col">Détail</th>
              <th scope="col">Traitement</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="muted">
                  Aucune anomalie.
                </td>
              </tr>
            )}
            {rows.map((a) => (
              <tr key={a.id}>
                <td>
                  <Badge tone={a.status === 'ouverte' ? 'danger' : 'muted'}>
                    {ANOMALY_LABELS[a.kind] ?? a.kind}
                  </Badge>
                  <span className="pl-sub">{formatDate(a.created_at)}</span>
                </td>
                <td>
                  <span className="pl-cell">
                    {a.detail}
                    {a.order_reference && (
                      <span className="pl-sub">
                        Ordre <span className="mono pl-ref">{a.order_reference}</span>
                      </span>
                    )}
                  </span>
                </td>
                <td>
                  {a.status === 'ouverte' ? (
                    <div className="pl-inline">
                      <input
                        aria-label="Résolution"
                        value={resolution[a.id] ?? ''}
                        onChange={(e) => setResolution({ ...resolution, [a.id]: e.target.value })}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={(resolution[a.id] ?? '').length < 5}
                        onClick={() => void resolve(a.id)}
                      >
                        Clore
                      </button>
                    </div>
                  ) : (
                    a.resolution
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ExemptionsTab() {
  const [rows, setRows] = useState<
    {
      id: string;
      application_reference: string;
      legal_basis: string;
      authority: string;
      evidence_ref: string;
      status: string;
      requested_at: string;
    }[]
  >([]);
  const [error, setError] = useState<ApiError | null>(null);
  const load = () => api<typeof rows>('/finance/exonerations').then(setRows).catch(setError);
  useEffect(() => {
    void load();
  }, []);
  const decide = async (id: string, verb: string) => {
    setError(null);
    try {
      await api(`/finance/exonerations/${id}/${verb}`, { method: 'POST' });
      await load();
    } catch (e) {
      setError(e as ApiError);
    }
  };
  return (
    <>
      <ErrorBox error={error} />
      <div className="card np table-wrap">
        <table>
          <caption className="visually-hidden">Exonérations</caption>
          <thead>
            <tr>
              <th scope="col">Demande</th>
              <th scope="col">Fondement</th>
              <th scope="col">Décision</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="muted">
                  Aucune exonération.
                </td>
              </tr>
            )}
            {rows.map((e) => (
              <tr key={e.id}>
                <td>
                  <span className="mono pl-ref">{e.application_reference}</span>
                </td>
                <td>
                  <span className="pl-cell">
                    {e.legal_basis} - {e.authority}
                    <span className="pl-sub">{e.evidence_ref}</span>
                  </span>
                </td>
                <td>
                  {e.status === 'demandee' ? (
                    <div className="pl-inline">
                      <button type="button" className="btn" onClick={() => void decide(e.id, 'accorder')}>
                        Accorder
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void decide(e.id, 'refuser')}
                      >
                        Refuser
                      </button>
                    </div>
                  ) : (
                    <Badge tone={e.status === 'accordee' ? 'ok' : 'muted'}>
                      {e.status === 'accordee' ? 'Accordée' : 'Refusée'}
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const TOTAL_LABELS: Record<string, string> = {
  recuperes: 'Paiements récupérés auprès du prestataire',
  confirmes_sans_timbre: 'Ordres confirmés sans timbre',
  traitees_sans_paiement: 'Demandes en traitement sans paiement ni exonération',
  anomalies_ouvertes: 'Anomalies ouvertes',
  montant_confirme_xaf: 'Montant confirmé (XAF)',
};

function ReconciliationTab() {
  const [result, setResult] = useState<{ started_at: string; totals: Record<string, number> } | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await api('/finance/rapprochement', { method: 'POST' }));
    } catch (e) {
      setError(e as ApiError);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card flow">
      <p className="muted" style={{ margin: 0 }}>
        Compare ordres, confirmations du prestataire, timbres et demandes ; interroge le prestataire pour les
        paiements dont la notification n’est pas arrivée. Prévu chaque jour en exploitation.
      </p>
      <div>
        <button type="button" className="btn" onClick={() => void run()} disabled={busy}>
          {busy ? 'Rapprochement…' : 'Lancer le rapprochement'}
        </button>
      </div>
      <ErrorBox error={error} />
      {result && (
        <dl className="details">
          {Object.entries(result.totals).map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt>{TOTAL_LABELS[k] ?? k.replace(/_/g, ' ')}</dt>
              <dd>{v.toLocaleString('fr-FR')}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function FeesTab() {
  const [rows, setRows] = useState<
    {
      id: string;
      type_code: string;
      amount_xaf: number;
      legal_basis: string;
      authority: string;
      effective_from: string;
      effective_until: string | null;
      status: string;
    }[]
  >([]);
  useEffect(() => {
    api<typeof rows>('/finance/tarifs')
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  return (
    <div className="card np table-wrap">
      <table>
        <caption className="visually-hidden">Barème</caption>
        <thead>
          <tr>
            <th scope="col">Démarche</th>
            <th scope="col">Montant</th>
            <th scope="col">Fondement et autorité</th>
            <th scope="col">Période</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.id}>
              <td>
                <span className="mono pl-ref">{f.type_code}</span>
              </td>
              <td>
                <span className="pl-cell">{xaf(f.amount_xaf)}</span>
              </td>
              <td>
                <span className="pl-cell">
                  {f.legal_basis}
                  <span className="pl-sub">{f.authority}</span>
                </span>
              </td>
              <td>
                depuis le {f.effective_from.slice(0, 10)}
                {f.effective_until ? ` jusqu’au ${f.effective_until.slice(0, 10)}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
